<template>
  <b-dropdown
    v-bind="$attrs"
    block
    lazy
    variant="outline-primary"
    data-test="select-date-range"
  >
    <template
      #button-content
    >
      <p
        class="mb-0"
      >
        <span>
          {{ getSelectedOption() }}: </span>
        <span
          v-if="startDateValue"
        >
          {{ formatDateForEoC(startDateValue) }}
        </span> - <span
          v-if="endDateValue"
        >
          {{ formatDateForEoC(endDateValue) }}
        </span>
      </p>
    </template>
    <b-dropdown-form>
      <b-row
        style="max-width:790px;"
        :style="{ width: (windowWidth * 0.70) + 'px' }"
      >
        <b-col
          cols="5"
        >
          <b-form-group
            id="start-date-group"
            label="Start Date"
            label-for="start-date-picker"
            label-class="form-label"
          >
            <template
              slot="label"
            >
              Start Date
            </template>
            <datepicker
              :value="startDateValue"
              @selected="setCustomStartDate($event)"
              placeholder="Select Date"
              format="dd/MM/yyyy"
              :open-date="new Date(startDateValue)"
              :highlighted="highlightedDates"
              :disabled-dates="disabledStartDates"
              :bootstrap-styling="true"
              :monday-first="true"
              :inline="true"
              data-test="datepicker-start"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="5"
        >
          <b-form-group
            id="end-date-group"
            label="End Date"
            label-for="end-date-picker"
            label-class="form-label"
          >
            <template
              slot="label"
            >
              End Date
            </template>
            <datepicker
              :value="endDateValue"
              @selected="setCustomEndDate($event)"
              placeholder="Select Date"
              format="dd/MM/yyyy"
              :open-date="new Date(endDateValue)"
              :highlighted="highlightedDates"
              :disabled-dates="disabledEndDates"
              :bootstrap-styling="true"
              :monday-first="true"
              :inline="true"
              data-test="datepicker-end"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="2"
        >
          <b-button
            v-for="(option, index) in datepickerOptions"
            :key="index"
            block
            :variant="selectedOption === option.toLowerCase() ? 'primary' : 'outline-primary'"
            @click="setSelectedOption(option.toLowerCase())"
          >
            {{ option }}
          </b-button>
          <b-button
            block
            variant="outline-primary"
            @click="setSelectedOption(defaultDates)"
          >
            Reset
          </b-button>
        </b-col>
      </b-row>
    </b-dropdown-form>
  </b-dropdown>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';

import dateHelper from '@/core/mixins/dateHelper';

import {
  startOfDay,
  endOfDay,
  startOfYesterday,
  endOfYesterday,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';
import Datepicker from 'vuejs-datepicker';
import _ from 'lodash';

export default {
  name: 'DatePickerDropdown',
  mixins: [
    dateHelper,
  ],
  components: {
    Datepicker,
  },
  props: {
    dropToSide: {
      type: String,
      required: false,
      default: 'right',
      validator(value) {
        return ['right', 'left'].includes(value);
      },
    },
    defaultDates: {
      type: String,
      required: false,
      default: '',
      validator(value) {
        return ['', 'today', 'yesterday', 'this week', 'last week', 'this month', 'last month'].includes(value);
      },
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      datepickerOptions: {
        today: 'Today',
        yesterday: 'Yesterday',
        thisWeek: 'This Week',
        lastWeek: 'Last Week',
        thisMonth: 'This Month',
        lastMonth: 'Last Month',
      },
      today: new Date(),
      selectedOption: '',
      selectedStartDate: -1,
      selectedEndDate: -1,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'order',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_ENDED,
    ]),
    minSrartDate() {
      return this.order.startDate || null;
    },
    maxEndDate() {
      return this.order.endDate || null;
    },
    startDateValue() {
      if (this.selectedStartDate !== -1) return this.convertDateToString(this.selectedStartDate);
      return this.minSrartDate || this.convertDateToString(this.today);
    },
    endDateValue() {
      if (this.selectedEndDate !== -1) return this.convertDateToString(this.selectedEndDate);
      return this.GET_IS_CAMPAIGN_ENDED || new Date(this.today) > new Date(this.maxEndDate) ? this.maxEndDate : this.convertDateToString(this.today);
    },
    highlightedDates() {
      return {
        from: new Date(this.startDateValue),
        to: new Date(this.endDateValue),
      };
    },
    disabledStartDates() {
      return {
        to: this.minSrartDate ? new Date(this.minSrartDate) : null,
        from: this.maxEndDate ? new Date(this.maxEndDate) : this.today,
      };
    },
    disabledEndDates() {
      return {
        to: this.minSrartDate ? new Date(this.minSrartDate) : null,
        from: this.maxEndDate ? new Date(this.maxEndDate) : this.today,
      };
    },
  },
  methods: {
    setCustomStartDate(date) {
      this.selectedStartDate = date;
      this.selectedOption = '';
      if (date > new Date(this.endDateValue)) this.selectedEndDate = date;
      this.emitUpdatedDates();
    },
    setCustomEndDate(date) {
      this.selectedEndDate = date;
      this.selectedOption = '';
      if (date < new Date(this.selectedStartDate)) this.selectedStartDate = date;
      this.emitUpdatedDates();
    },
    setSelectedOption(value) {
      const { today } = this;
      this.selectedOption = value;
      switch (value) {
        case 'today':
          this.selectedStartDate = startOfDay(today);
          this.selectedEndDate = endOfDay(today);
          this.emitUpdatedDates();
          break;
        case 'yesterday':
          this.selectedStartDate = startOfYesterday();
          this.selectedEndDate = endOfYesterday();
          this.emitUpdatedDates();
          break;
        case 'this week':
          this.selectedStartDate = startOfWeek(today, { weekStartsOn: 1 });
          this.selectedEndDate = endOfDay(today);
          this.emitUpdatedDates();
          break;
        case 'last week':
          this.selectedStartDate = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
          this.selectedEndDate = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
          this.emitUpdatedDates();
          break;
        case 'this month':
          this.selectedStartDate = startOfMonth(today, { weekStartsOn: 1 });
          this.selectedEndDate = endOfDay(today);
          this.emitUpdatedDates();
          break;
        case 'last month':
          this.selectedStartDate = startOfMonth(subMonths(today, 1), { weekStartsOn: 1 });
          this.selectedEndDate = endOfMonth(subMonths(today, 1), { weekStartsOn: 1 });
          this.emitUpdatedDates();
          break;
        default:
          this.selectedStartDate = -1;
          this.selectedEndDate = -1;
          this.emitUpdatedDates();
          break;
      }
    },
    emitUpdatedDates() {
      this.$emit('updateDates', this.startDateValue, this.endDateValue);
    },
    getSelectedOption() {
      if (this.selectedOption) return this.datepickerOptions[`${_.camelCase(this.selectedOption)}`];
      if (this.order.startDate === this.startDateValue
        && this.order.endDate === this.endDateValue) return 'Campaign Dates';
      return 'Custom Dates';
    },
  },
  mounted() {
    if (!this.minSrartDate || !this.maxEndDate) {
      this.setSelectedOption(this.defaultDates);
    }
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .dropdown-menu-right {
    transform: unset !important;
    left: unset !important;
    top: 38px !important;
    right: 0px !important;
  }
  *.b-dropdown-form:focus {
    outline: 0px transparent !important;
  }
  .vdp-datepicker{
    .vdp-datepicker__calendar {
      width: 100% !important;
      min-width: 265px !important;
    }
  }
  .dropdown-toggle {
    display: flex;
    align-items: center;
  }
  .dropdown-toggle p {
    display: inline-block;
  }
  .dropdown-toggle:after {
    margin-left: auto;
  }
}
</style>
